import { createAsyncThunk } from '@reduxjs/toolkit';

import type { AppDispatch, RootState } from '@/store';

import type {
  EntradaDaConversa,
  TipoDeComando,
  RespostaDeInteracao,
  ResultadoDeComando,
  ViaDoEnvio,
  ConfigComSimulacao,
  EnvioDaConversa,
} from '../../types';
import {
  intencaoReaproveitavel,
  intencaoNova,
  desfechoDesconhecido,
} from '../../utils/intencaoDeComando';
import { reagirARecusaDoComando } from '../ciclo/actions';
import { intencaoRegistrada } from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { servicosDaSimulacao } from '../servicosDaSimulacao';

const comandoDaEntrada = (entrada: EntradaDaConversa): TipoDeComando =>
  entrada.tipo === 'texto' ? 'enviarTexto' : 'responderInteracao';

/** Só os dois campos: o `tipo` da entrada não vai ao core nem entra na assinatura da intenção. */
const respostaDaInteracao = ({
  mensagemId,
  opcaoId,
}: RespostaDeInteracao): RespostaDeInteracao => ({
  mensagemId,
  opcaoId,
});

const corpoDaEntrada = (entrada: EntradaDaConversa) =>
  entrada.tipo === 'texto' ? { texto: entrada.texto } : respostaDaInteracao(entrada);

const chamarEntrada = (
  servicos: ReturnType<typeof servicosDaSimulacao>,
  simulacaoId: string,
  entrada: EntradaDaConversa,
  chave: string,
) =>
  entrada.tipo === 'texto'
    ? servicos.enviarTexto(simulacaoId, entrada.texto, chave)
    : servicos.responderInteracao(simulacaoId, respostaDaInteracao(entrada), chave);

export const executarEntrada = createAsyncThunk<
  ResultadoDeComando & { simulacaoId: string },
  { entrada: EntradaDaConversa; via: ViaDoEnvio; repetir: boolean },
  ConfigComSimulacao
>(
  'negociacaoSimulacao/executarEntrada',
  async ({ entrada, via, repetir }, { dispatch, getState, rejectWithValue }) => {
    const { simulacao, envio, intencoes } = estadoDaFeature(getState());
    const simulacaoId = simulacao?.id ?? '';
    const comandoId = envio?.comandoId ?? '';
    const tipo: TipoDeComando =
      via === 'novaTentativa' ? 'novaTentativa' : comandoDaEntrada(entrada);
    const corpo = via === 'novaTentativa' ? { comandoId } : corpoDaEntrada(entrada);
    const intencao = repetir ? intencaoReaproveitavel(intencoes, tipo, corpo) : intencaoNova(corpo);
    const servicos = servicosDaSimulacao(getState());
    dispatch(intencaoRegistrada({ tipo, intencao }));
    try {
      const resultado =
        via === 'novaTentativa'
          ? await servicos.novaTentativa(simulacaoId, comandoId, intencao.chave)
          : await chamarEntrada(servicos, simulacaoId, entrada, intencao.chave);
      return { ...resultado, simulacaoId };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha(tipo, erro, falha, { simulacaoId });
      if (estadoDaFeature(getState()).simulacao?.id === simulacaoId)
        reagirARecusaDoComando(falha, simulacaoId, dispatch);
      return rejectWithValue(falha, { simulacaoId });
    }
  },
  {
    condition: ({ via }, { getState }) => {
      const { simulacao, envio } = estadoDaFeature(getState());
      if (simulacao?.status !== 'ATIVA' || envio?.status === 'enviando') return false;
      return via === 'envio' || Boolean(envio?.comandoId);
    },
  },
);

export const enviarTextoDaSimulacao = (texto: string) =>
  executarEntrada({ entrada: { tipo: 'texto', texto }, via: 'envio', repetir: false });

export const responderInteracaoDaSimulacao = (mensagemId: string, opcaoId: string) =>
  executarEntrada({
    entrada: { tipo: 'interacao', mensagemId, opcaoId },
    via: 'envio',
    repetir: false,
  });

/**
 * "Tentar novamente" da última entrada:
 * - rede caída: repete a mesma chamada com a mesma chave;
 * - comando que o core marcou FALHOU: vai para nova-tentativa, que reaproveita o mensagemId
 *   (CA-9), e cada nova tentativa é uma intenção nova;
 * - 5xx ou RODADA_EM_CURSO sem comandoId: a mensagem pode estar gravada, e só a mesma chave evita
 *   gravá-la de novo;
 * - recusa 4xx sem comando (texto inválido, opção fora do estado): nada foi gravado, sai como
 *   intenção nova.
 */
const planejarNovaTentativa = (envio: EnvioDaConversa): { via: ViaDoEnvio; repetir: boolean } => {
  if (envio.erro?.status === 0) return { via: envio.via, repetir: true };
  if (envio.comandoId) return { via: 'novaTentativa', repetir: false };
  if (desfechoDesconhecido(envio.erro) || envio.erro?.codigo === 'RODADA_EM_CURSO') {
    return { via: envio.via, repetir: true };
  }
  return { via: 'envio', repetir: false };
};

export const tentarNovamenteEntrada = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { envio } = estadoDaFeature(getState());
  if (envio?.status !== 'falhou') return;
  return dispatch(executarEntrada({ entrada: envio.entrada, ...planejarNovaTentativa(envio) }));
};
